import React, { useState, useEffect } from "react";
import { 
  AlertTriangle, CheckCircle, XCircle, TrendingUp, Clock, Phone, 
  ArrowUpRight, ArrowDownRight, CreditCard, Calendar, Building, 
  Mail, MessageCircle, HelpCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Demo API key for testing - in production, this would come from auth context
const DEMO_API_KEY = "test_key_123";

export function BillingPage() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/billing/overview`, {
        headers: { "X-API-Key": DEMO_API_KEY }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to fetch billing data: ${res.status}`);
      }
      
      const data = await res.json();
      setBilling(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800">Error Loading Billing</h3>
        <p className="text-red-600 mt-2">{error}</p>
        <button 
          onClick={fetchBillingData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const { plan, usage, notifications, client } = billing || {};
  
  // Calculate estimated invoice
  const overageMinutes = Math.max(0, (usage?.minutes_used || 0) - (usage?.minutes_included || 0));
  const overageCost = overageMinutes * (plan?.overage_per_min || 0);
  const estimatedInvoice = (plan?.price_monthly || 0) + overageCost;

  // Status helpers
  const getUsageStatusColor = (status) => {
    switch (status) {
      case "ok": return "text-emerald-600 bg-emerald-50 border-emerald-200";
      case "warning": return "text-amber-600 bg-amber-50 border-amber-200";
      case "critical": return "text-orange-600 bg-orange-50 border-orange-200";
      case "exceeded": return "text-red-600 bg-red-50 border-red-200";
      default: return "text-slate-600 bg-slate-50 border-slate-200";
    }
  };

  const getUsageStatusIcon = (status) => {
    switch (status) {
      case "ok": return <CheckCircle className="w-5 h-5" />;
      case "warning": return <AlertTriangle className="w-5 h-5" />;
      case "critical": return <AlertTriangle className="w-5 h-5" />;
      case "exceeded": return <XCircle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const usagePercent = Math.min(100, usage?.usage_percent || 0);
  const progressColor = usagePercent >= 100 ? "bg-red-500" : usagePercent >= 90 ? "bg-orange-500" : usagePercent >= 70 ? "bg-amber-500" : "bg-emerald-500";

  // Billing cycle display
  const billingCycle = client?.billing_cycle || plan?.billing_cycle || "monthly";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Billing & Usage</h1>
          <p className="text-slate-600 text-sm mt-1">Monitor your plan usage and manage your subscription</p>
        </div>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${getUsageStatusColor(usage?.status)}`}>
          {getUsageStatusIcon(usage?.status)}
          <span className="font-semibold capitalize">{usage?.status || "Unknown"}</span>
        </div>
      </div>

      {/* Call Admission Status Banner */}
      {!usage?.allowed && (
        <div className="bg-red-600 text-white rounded-xl p-4 flex items-center gap-4">
          <XCircle className="w-8 h-8 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-bold text-lg">Calls Are Currently Blocked</h3>
            <p className="text-red-100">Your account has reached its usage limit. Upgrade your plan to resume accepting calls.</p>
          </div>
          <button 
            onClick={() => setShowUpgradeModal(true)}
            className="px-6 py-2 bg-white text-red-600 font-bold rounded-lg hover:bg-red-50"
          >
            Upgrade Now
          </button>
        </div>
      )}

      {/* Status Chips Row */}
      <div className="flex flex-wrap gap-3">
        <StatusChip 
          label="Setup Fee" 
          status={client?.setup_fee_status || "unknown"}
          icon={CreditCard}
        />
        <StatusChip 
          label="Subscription" 
          status={client?.subscription_status || "pending"}
          icon={Calendar}
        />
        <StatusChip 
          label="Billing Cycle" 
          status={billingCycle}
          icon={Clock}
          isInfo
        />
        <StatusChip 
          label="Calls" 
          status={usage?.allowed ? "allowed" : "blocked"}
          icon={Phone}
        />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current Plan Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Current Plan</h3>
          <div className="mt-4">
            <p className="text-2xl font-bold text-slate-900 capitalize">
              {plan?.plan_id?.replace("_", " ") || "Unknown"}
            </p>
            <p className="text-slate-600 text-sm mt-1">
              {plan?.monthly_minutes_included || 0} minutes/month included
            </p>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">
                  {billingCycle === "yearly" ? "Yearly Price" : "Monthly Price"}
                </span>
                <span className="font-semibold">
                  ${billingCycle === "yearly" ? (plan?.price_yearly || plan?.price_monthly * 10) : plan?.price_monthly || 0}
                  /{billingCycle === "yearly" ? "yr" : "mo"}
                </span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-slate-500">Overage Rate</span>
                <span className="font-semibold">${plan?.overage_per_min?.toFixed(2) || "0.00"}/min</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-slate-500">Hard Cap</span>
                <span className="font-semibold">{plan?.hard_cap_minutes || usage?.hard_cap || "N/A"} min</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-slate-500">Locations</span>
                <span className="font-semibold">
                  {client?.locations_used || 1} / {plan?.max_locations === -1 ? "∞" : plan?.max_locations || 1}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Usage Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Usage This Month</h3>
          <div className="mt-4">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-slate-900">
                {Math.round(usage?.minutes_used || 0)}
              </span>
              <span className="text-slate-500">/ {usage?.minutes_included || 0} min</span>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${progressColor} transition-all duration-500`}
                  style={{ width: `${Math.min(100, usagePercent)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>{usagePercent.toFixed(1)}% used</span>
                <span>{Math.max(0, Math.round(usage?.minutes_remaining || 0))} min remaining</span>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500">Calls</p>
                <p className="text-lg font-semibold flex items-center gap-1">
                  <Phone className="w-4 h-4 text-blue-500" />
                  {usage?.calls_count || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Projected</p>
                <p className="text-lg font-semibold flex items-center gap-1">
                  <TrendingUp className="w-4 h-4 text-purple-500" />
                  {Math.round(usage?.projected_monthly || 0)} min
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Estimated Invoice Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Estimated Invoice</h3>
          <div className="mt-4">
            <p className="text-3xl font-bold text-slate-900">
              ${estimatedInvoice.toFixed(2)}
            </p>
            <p className="text-slate-500 text-sm mt-1">for {usage?.month || "this month"}</p>
            
            {/* Breakdown */}
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Base subscription</span>
                <span className="font-medium">${plan?.price_monthly || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Overage ({Math.round(overageMinutes)} min)</span>
                <span className="font-medium text-orange-600">
                  {overageCost > 0 ? `+$${overageCost.toFixed(2)}` : "$0.00"}
                </span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-slate-100">
                <span className="font-semibold">Total (estimated)</span>
                <span className="font-bold">${estimatedInvoice.toFixed(2)}</span>
              </div>
            </div>
            
            <p className="text-xs text-slate-400 mt-4">
              * Estimate based on current usage. Final invoice may vary.
            </p>
          </div>
        </div>
      </div>

      {/* Notifications Section */}
      {notifications && notifications.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Usage Alerts</h3>
          <div className="space-y-3">
            {notifications.map((notif, idx) => (
              <div 
                key={notif.id || idx}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  notif.type.includes("exceeded") ? "bg-red-50 border-red-200" :
                  notif.type.includes("100") ? "bg-orange-50 border-orange-200" :
                  notif.type.includes("90") ? "bg-amber-50 border-amber-200" :
                  "bg-blue-50 border-blue-200"
                }`}
              >
                <AlertTriangle className={`w-5 h-5 ${
                  notif.type.includes("exceeded") ? "text-red-500" :
                  notif.type.includes("100") ? "text-orange-500" :
                  notif.type.includes("90") ? "text-amber-500" :
                  "text-blue-500"
                }`} />
                <div className="flex-1">
                  <p className="font-medium text-slate-800">
                    {notif.type === "usage_exceeded" ? "Hard cap exceeded - calls blocked" :
                     notif.type === "usage_100" ? "100% of included minutes used" :
                     notif.type === "usage_90" ? "90% usage warning" :
                     notif.type === "usage_70" ? "70% usage notice" :
                     notif.type}
                  </p>
                  <p className="text-xs text-slate-500">
                    {Math.round(notif.minutes_used)} / {notif.minutes_included} minutes ({notif.usage_percent.toFixed(1)}%)
                  </p>
                </div>
                <span className="text-xs text-slate-400">{new Date(notif.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plan Actions */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Manage Subscription</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 shadow-lg"
          >
            <ArrowUpRight className="w-5 h-5" />
            Upgrade Plan
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Effective Immediately</span>
          </button>
          
          <button
            onClick={() => setShowDowngradeModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 border border-slate-200"
          >
            <ArrowDownRight className="w-5 h-5" />
            Downgrade Plan
            <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Applies Next Cycle</span>
          </button>

          <button
            onClick={() => setShowAddLocationModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 border border-slate-200"
          >
            <Building className="w-5 h-5" />
            Add Location
          </button>
          
          <button
            onClick={() => setShowCancelModal(true)}
            className="flex items-center gap-2 px-6 py-3 text-red-600 font-semibold rounded-lg hover:bg-red-50 border border-red-200"
          >
            Cancel Subscription
          </button>
        </div>
      </div>

      {/* Contact Support Section */}
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <HelpCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Need Help?</h3>
            <p className="text-slate-600 text-sm mt-1">
              Our team is here to help with billing questions, plan changes, or any other concerns.
            </p>
            <div className="flex flex-wrap gap-3 mt-4">
              <a 
                href="mailto:support@calyro.ai"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 text-sm font-medium"
              >
                <Mail className="w-4 h-4" />
                support@calyro.ai
              </a>
              <button
                onClick={() => setShowContactModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                <MessageCircle className="w-4 h-4" />
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showUpgradeModal && (
        <Modal title="Upgrade Plan" onClose={() => setShowUpgradeModal(false)}>
          <p className="text-slate-600 mb-4">
            Upgrades are effective immediately. Your new plan limits will apply right away, 
            and you'll be charged a prorated amount for the remainder of this billing cycle.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-800 font-medium">Stripe checkout coming soon</p>
            <p className="text-blue-600 text-sm">Payment integration is being set up. Contact support to upgrade now.</p>
          </div>
          <button 
            onClick={() => setShowUpgradeModal(false)}
            className="w-full py-2 bg-slate-100 rounded-lg hover:bg-slate-200"
          >
            Close
          </button>
        </Modal>
      )}

      {showDowngradeModal && (
        <Modal title="Downgrade Plan" onClose={() => setShowDowngradeModal(false)}>
          <p className="text-slate-600 mb-4">
            Downgrades take effect at the start of your <strong>next billing cycle</strong>. 
            You'll continue to have access to your current plan's limits until then.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-amber-800 font-medium">Important</p>
            <p className="text-amber-600 text-sm">
              If your current usage exceeds the new plan's limits, you may incur overages 
              when the downgrade takes effect.
            </p>
          </div>
          <button 
            onClick={() => setShowDowngradeModal(false)}
            className="w-full py-2 bg-slate-100 rounded-lg hover:bg-slate-200"
          >
            Close
          </button>
        </Modal>
      )}

      {showAddLocationModal && (
        <Modal title="Add Location" onClose={() => setShowAddLocationModal(false)}>
          <p className="text-slate-600 mb-4">
            Add a new location to your account. Each location can have its own phone number and AI agent.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
            <p className="text-slate-700">
              <strong>Current:</strong> {client?.locations_used || 1} location(s)<br/>
              <strong>Plan limit:</strong> {plan?.max_locations === -1 ? "Unlimited" : plan?.max_locations || 1} location(s)
            </p>
          </div>
          {(plan?.max_locations === -1 || (client?.locations_used || 1) < (plan?.max_locations || 1)) ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 font-medium">Contact support to add a location</p>
              <p className="text-blue-600 text-sm">Email support@calyro.ai or use the contact form.</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 font-medium">Location limit reached</p>
              <p className="text-amber-600 text-sm">Upgrade to a higher plan for more locations.</p>
            </div>
          )}
          <button 
            onClick={() => setShowAddLocationModal(false)}
            className="w-full py-2 bg-slate-100 rounded-lg hover:bg-slate-200"
          >
            Close
          </button>
        </Modal>
      )}

      {showCancelModal && (
        <Modal title="Cancel Subscription" onClose={() => setShowCancelModal(false)}>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800 font-medium">Warning: This will disable your AI agent</p>
            <p className="text-red-600 text-sm mt-1">
              Cancellation takes effect at the end of your current billing period. 
              After that, your AI agent will stop answering calls.
            </p>
          </div>
          <p className="text-slate-600 mb-4">
            Need help? Contact support@calyro.ai before canceling - we may be able to 
            find a plan that better fits your needs.
          </p>
          <div className="flex gap-3">
            <button 
              onClick={() => setShowCancelModal(false)}
              className="flex-1 py-2 bg-slate-100 rounded-lg hover:bg-slate-200"
            >
              Keep Subscription
            </button>
            <button 
              className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              onClick={() => {
                alert("Cancellation flow will be implemented with Stripe integration");
                setShowCancelModal(false);
              }}
            >
              Proceed to Cancel
            </button>
          </div>
        </Modal>
      )}

      {showContactModal && (
        <Modal title="Contact Support" onClose={() => setShowContactModal(false)}>
          <p className="text-slate-600 mb-4">
            Our support team typically responds within 24 hours during business days.
          </p>
          <div className="space-y-3 mb-4">
            <a 
              href="mailto:support@calyro.ai"
              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
            >
              <Mail className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-slate-900">Email Support</p>
                <p className="text-sm text-slate-500">support@calyro.ai</p>
              </div>
            </a>
            <a 
              href="tel:+15551234567"
              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
            >
              <Phone className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="font-medium text-slate-900">Phone Support</p>
                <p className="text-sm text-slate-500">(555) 123-4567</p>
              </div>
            </a>
          </div>
          <p className="text-xs text-slate-400">
            Support hours: Monday - Friday, 9am - 6pm EST
          </p>
          <button 
            onClick={() => setShowContactModal(false)}
            className="w-full py-2 bg-slate-100 rounded-lg hover:bg-slate-200 mt-4"
          >
            Close
          </button>
        </Modal>
      )}
    </div>
  );
}

// Status Chip Component
function StatusChip({ label, status, icon: Icon, isInfo }) {
  const getChipStyle = () => {
    if (isInfo) return "bg-slate-100 text-slate-700 border-slate-200";
    
    switch (status) {
      case "paid":
      case "active":
      case "allowed":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "pending":
      case "unpaid":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "blocked":
      case "suspended":
      case "canceled":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm ${getChipStyle()}`}>
      <Icon className="w-4 h-4" />
      <span className="font-medium">{label}:</span>
      <span className="capitalize">{status}</span>
    </div>
  );
}

// Simple Modal Component
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default BillingPage;
